import PropTypes from 'prop-types';
import styles from './FJAudioPreview.module.less';
import { useRef, useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import FJPlayController from '../../../common/components/FJVideoPlayer/playController/FJController.jsx';
import FJResourceItemLoading from '../../../common/components/FJResourceItemLoading/FJResourceItemLoading.jsx';
import ReloadIcon from '../../../assets/svg/reload.svg?react';
import FJUtil from '../../../util/FJUtil.js';

const FJAudioPreview = (props, ref) => {
    const { audioUrl, coverUrl, active } = props;
    const audioRef = useRef(null);
    const playCrlEle = useRef(null);
    const _timer = useRef(null);
    const [isLoaded, setIsLoaded] = useState(false);
    const [isError, setIsError] = useState(false);
    const [isShowLoading, setIsShowLoading] = useState(false);
    const [audioDuration, setAudioDuration] = useState(0);
    const [useUrl, setUseUrl] = useState(null);
    const [useCoverUrl, setUseCoverUrl] = useState(coverUrl);

    useImperativeHandle(ref, () => ({
        reloadCover: reloadCover,
        reset: () => {
            if (playCrlEle.current) {
                playCrlEle.current.reset();
            }
        },
    }));

    useEffect(() => {
        setUseCoverUrl(coverUrl);
    }, [coverUrl]);

    useEffect(() => {
        _pause();
    }, [audioUrl]);

    useEffect(() => {
        if (!active) {
            _pause();
            if (playCrlEle.current) {
                playCrlEle.current.reset();
            }
        }
    }, [active]);

    useEffect(() => {
        if (playCrlEle.current) {
            playCrlEle.current.reset();
        }
        setUseUrl(audioUrl);
        setIsLoaded(false);
        setIsShowLoading(true);
        setIsError(false);
        _clearProgressTimer();
    }, [audioUrl]);

    const reloadCover = () => {
        setUseCoverUrl(FJUtil.getReloadNewUrl(coverUrl));
    };

    const _clearProgressTimer = () => {
        if (_timer.current) {
            clearInterval(_timer.current);
        }
    };

    const _updateProgress = () => {
        _clearProgressTimer();
        _timer.current = setInterval(() => {
            try {
                const currentTime = audioRef.current.currentTime;
                const duration = audioRef.current.duration;
                const progress = Math.min(currentTime / duration, 1);
                playCrlEle.current.updateProgress(progress);
            } catch (e) {
                console.log(e);
                _clearProgressTimer();
            }
        }, 40);
    };

    const onReload = () => {
        setIsLoaded(false);
        setIsShowLoading(true);
        setIsError(false);
        setUseUrl(audioUrl + '?v=' + new Date().getTime());
    };

    const onLoadedData = () => {
        setIsLoaded(true);
        setIsShowLoading(false);
        setAudioDuration(audioRef.current.duration);
    };

    const onLoadStart = () => {
        setIsLoaded(false);
        setIsError(false);
        setIsShowLoading(true);
    };

    const onError = () => {
        setIsError(true);
        setIsShowLoading(false);
    };

    const onEnded = () => {
        if (playCrlEle.current) {
            _pause();
            playCrlEle.current.reset();
        }
    };

    const _play = () => {
        audioRef.current.play();
        if (playCrlEle.current) {
            playCrlEle.current.changePlayState(true);
        }
        _updateProgress();
    };

    const _pause = () => {
        try {
            audioRef.current.pause();
            if (playCrlEle.current) {
                playCrlEle.current.changePlayState(false);
            }
            _clearProgressTimer();
        } catch (e) {
            console.log(e);
        }
    };

    const _sliderDataChange = data => {
        audioRef.current.currentTime = audioDuration * data;
    };

    const _volumeChange = value => {
        audioRef.current.volume = value;
    };

    return (
        <div className={styles.box}>
            {isShowLoading && !isLoaded && <FJResourceItemLoading style={{ borderRadius: 0 }} />}
            {isError && (
                <div className={styles.retry_box}>
                    <ReloadIcon className={styles.reload_icon} onClick={onReload} />
                </div>
            )}
            <img className={styles.bottom_img} src={useCoverUrl} alt='' />
            <div className={styles.mask} />
            <img className={styles.cover} src={useCoverUrl} alt='' />
            <audio
                src={useUrl}
                ref={audioRef}
                onLoadedData={onLoadedData}
                onLoadStart={onLoadStart}
                onError={onError}
                onEnded={onEnded}
            />
            <div style={{ opacity: isLoaded && !isError ? 1 : 0 }}>
                <FJPlayController
                    ref={playCrlEle}
                    duration={audioDuration}
                    play={_play}
                    pause={_pause}
                    dataChange={_sliderDataChange}
                    previewVolumeChange={_volumeChange}
                    scope='previewModalAudio'
                    isShowPlayBtn={false}
                    isShowFullScreen={false}
                    isShowVolume={true}
                    isKeySpace={true}
                />
            </div>
        </div>
    );
};
FJAudioPreview.propTypes = {
    audioUrl: PropTypes.string,
    coverUrl: PropTypes.string,
    active: PropTypes.bool,
};
export default forwardRef(FJAudioPreview);
